/* eslint-disable react/destructuring-assignment */
import { Text } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { useRouter } from 'next/router'
import ReactPaginate from 'react-paginate'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { DEFAULT_PAGE_NUMBER_QUERY_KEY, pageSize } from './constants'

type PaginationPropsBase = {
  totalItemsCount: number
  itemsPerPage?: number
}

type PaginationPropsWithUrlScheme = PaginationPropsBase & {
  scheme: 'url'
  urlHash?: string
  pageRangeDisplayed?: number
}

type PaginationPropsWithControlledScheme = PaginationPropsBase & {
  scheme: 'controlled'
  currentPage: number
  onPageChange?: (page: number) => void
}

type PaginationProps = PaginationPropsWithUrlScheme | PaginationPropsWithControlledScheme

const DEFAULT_PAGE_RANGE_DISPLAYED = 5
const PaginationStyles = css`
  text-align: center;
  ul {
    display: flex;
    padding: 0;
    justify-content: center;
  }
  li {
    display: block;
    border: 1px solid ${chakraTokenToVar('border.emphasized')};
    border-right: 0;
    &:last-of-type {
      border-right: 1px solid ${chakraTokenToVar('border.emphasized')};
    }
    &.active {
      background: ${chakraTokenToVar('primary.solid')};
    }
    a {
      padding: 5px 10px;
      display: block;
      font: inherit;
      color: ${chakraTokenToVar('fg')};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
`

export function useOffset(itemsPerPage = pageSize): number {
  const router = useRouter()
  const page = Number.parseInt(`${router.query[DEFAULT_PAGE_NUMBER_QUERY_KEY]}`, 10) || 1
  const offset = (page - 1) * itemsPerPage
  return offset
}

export default function Pagination(props: PaginationProps): JSX.Element | null {
  const router = useRouter()
  const urlPage = Number.parseInt(`${router.query[DEFAULT_PAGE_NUMBER_QUERY_KEY]}`, 10) || 1

  const { totalItemsCount, scheme, itemsPerPage = pageSize } = props

  if (totalItemsCount <= itemsPerPage) {
    return null
  }

  const currentPage = scheme === 'url' ? urlPage : props.currentPage
  const displayedPageRange =
    scheme === 'url' ? (props.pageRangeDisplayed ?? DEFAULT_PAGE_RANGE_DISPLAYED) : undefined
  const pageCount = Math.ceil(totalItemsCount / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(startItem + itemsPerPage - 1, totalItemsCount)

  const handlePageChange = async (event: { selected: number }) => {
    const newPage = event.selected + 1

    if (scheme === 'controlled' && props.onPageChange != null) {
      props.onPageChange(newPage)
    } else if (scheme === 'url') {
      await router.push({
        query: {
          ...router.query,
          [DEFAULT_PAGE_NUMBER_QUERY_KEY]: newPage.toString(),
        },
        hash: props.urlHash,
      })
    }
  }

  return (
    <div css={PaginationStyles}>
      <ReactPaginate
        pageRangeDisplayed={displayedPageRange}
        marginPagesDisplayed={1}
        forcePage={currentPage - 1}
        pageCount={pageCount}
        breakLabel="..."
        activeClassName="active"
        previousLabel="⟨"
        nextLabel="⟩"
        onPageChange={handlePageChange}
      />
      <Text>
        <b>
          {startItem} - {endItem}
        </b>{' '}
        of <b>{totalItemsCount}</b>
      </Text>
    </div>
  )
}
